import { lazy } from "react";
import { Route, Routes } from "react-router";
import LegacyTeamRedirect from "~/components/LegacyTeamRedirect";
import OnboardingGuard from "~/components/OnboardingGuard";
import TeamRouteGuard from "~/components/TeamRouteGuard";
import { TEAM_SCOPED_SECTIONS } from "~/lib/team-path";
import { Navigate } from "~/lib/team-router";
import App from "~/root";
import AccountLayout from "~/views/layout/AccountLayout";
import AgencyLayout from "~/views/layout/AgencyLayout";
import AIVisibilityLayout from "~/views/layout/AIVisibilityLayout";
import AppLayout from "~/views/layout/AppLayout";
import ChatLayout from "~/views/layout/ChatLayout";
import GuestLayout from "~/views/layout/GuestLayout";
import OnboardingLayout from "~/views/layout/OnboardingLayout";

const Home = lazy(() => import("~/views/home"));
const Login = lazy(() => import("~/views/login"));
const Signup = lazy(() => import("~/views/signup"));
const SignupComplete = lazy(() => import("~/views/signup.complete"));
const Chat = lazy(() => import("~/views/chat"));
const KeywordClusters = lazy(() => import("~/views/keyword-clusters"));
const Library = lazy(() => import("~/views/library"));
const KeywordCluster = lazy(() => import("~/views/keyword-cluster"));
const Shopping = lazy(() => import("~/views/shopping"));
const ShoppingProduct = lazy(() => import("~/views/shopping.product"));
const Profile = lazy(() => import("~/views/profile"));
const Mcp = lazy(() => import("~/views/mcp"));
const Team = lazy(() => import("~/views/team"));
const SettingsLayout = lazy(() => import("~/views/layout/SettingsLayout"));
const SettingsGeneral = lazy(() => import("~/views/settings.general"));
const SettingsWebsiteSitemap = lazy(
  () => import("~/views/settings.website.sitemap")
);
const SettingsWebsiteCompetitors = lazy(
  () => import("~/views/settings.website.competitors")
);
const SettingsIntegrations = lazy(
  () => import("~/views/settings.integrations")
);
const SettingsIntegrationsCloudflare = lazy(
  () => import("~/views/settings.integrations.cloudflare")
);
const SettingsIntegrationsVercel = lazy(
  () => import("~/views/settings.integrations.vercel")
);
const SettingsIntegrationsWordPress = lazy(
  () => import("~/views/settings.integrations.wordpress")
);
const SettingsIntegrationsSanity = lazy(
  () => import("~/views/settings.integrations.sanity")
);
const SettingsIntegrationsShopify = lazy(
  () => import("~/views/settings.integrations.shopify")
);
const SettingsIntegrationsGoogleMerchantCenter = lazy(
  () => import("~/views/settings.integrations.google-merchant-center")
);
const SettingsIntegrationsProductFeed = lazy(
  () => import("~/views/settings.integrations.product-feed")
);
const SettingsGoogleSearchConsole = lazy(
  () => import("~/views/settings.google-search-console")
);
const SettingsBingWebmaster = lazy(
  () => import("~/views/settings.bing-webmaster-tools")
);
const SettingsGoogleAnalytics = lazy(
  () => import("~/views/settings.google-analytics")
);
const SettingsBilling = lazy(() => import("~/views/settings.billing"));
const SettingsBillingSubscribe = lazy(
  () => import("~/views/settings.billing.subscribe")
);
const SettingsUsage = lazy(() => import("~/views/settings.usage"));
const SettingsBrandGuidelines = lazy(
  () => import("~/views/settings.brand-guidelines")
);
const AIVisibility = lazy(() => import("~/views/ai-visibility.home"));
const AIVisibilityPrompts = lazy(() => import("~/views/ai-visibility.prompts"));
const AIVisibilityPromptsSuggestions = lazy(
  () => import("~/views/ai-visibility.prompts.suggestions")
);
const AIVisibilityPromptsRemovals = lazy(
  () => import("~/views/ai-visibility.prompts.removals")
);
const SettingsPromptTags = lazy(() => import("~/views/settings.prompt-tags"));
const SettingsAIVisibility = lazy(
  () => import("~/views/settings.ai-visibility")
);
const SettingsRunHistory = lazy(() => import("~/views/settings.run-history"));
const SettingsRunHistoryDetail = lazy(
  () => import("~/views/settings.run-history-detail")
);
const AIVisibilitySources = lazy(() => import("~/views/ai-visibility.sources"));
const AIVisibilityFanouts = lazy(() => import("~/views/ai-visibility.fanouts"));
const AIVisibilitySignals = lazy(() => import("~/views/ai-visibility.signals"));

const AIVisibilityBotTraffic = lazy(
  () => import("~/views/ai-visibility.ai-traffic")
);
const AIVisibilityLLMResponse = lazy(
  () => import("~/views/ai-visibility.llm-response")
);
const Onboarding = lazy(() => import("~/views/onboarding"));
const OnboardingWebsite = lazy(() => import("~/views/onboarding.website"));
const OnboardingCompetitors = lazy(
  () => import("~/views/onboarding.competitors")
);
const OnboardingPrompts = lazy(() => import("~/views/onboarding.prompts"));
const OnboardingStore = lazy(() => import("~/views/onboarding.store"));
const OnboardingCatalogPrompts = lazy(
  () => import("~/views/onboarding.catalog-prompts")
);
const OnboardingQuestions = lazy(() => import("~/views/onboarding.questions"));
const OnboardingIntegrations = lazy(
  () => import("~/views/onboarding.integrations")
);
const CreateNewTeam = lazy(() => import("~/views/teams.create-new"));
const RootRedirect = lazy(() => import("~/views/root-redirect"));
const FoundryBlogs = lazy(() => import("~/views/foundry.blogs"));
const FoundryBlogEditor = lazy(() => import("~/views/foundry.blog-editor"));
const FoundryLandingPages = lazy(() => import("~/views/foundry.landing-pages"));
const FoundryLandingPageEditor = lazy(
  () => import("~/views/foundry.landing-page-editor")
);
const FoundryTools = lazy(() => import("~/views/foundry.tools"));
const FoundryToolEditor = lazy(() => import("~/views/foundry.tool-editor"));
const FoundryLeadForms = lazy(() => import("~/views/foundry.lead-forms"));
const FoundryLeadFormEditor = lazy(
  () => import("~/views/foundry.lead-form-editor")
);
const FoundryLayout = lazy(() => import("~/views/layout/FoundryLayout"));
const FoundryControlPanelLayout = lazy(
  () => import("~/views/layout/FoundryControlPanelLayout")
);
const FoundryControlPanelGeneral = lazy(
  () => import("~/views/foundry.control-panel.general")
);
const FoundryControlPanelAnalytics = lazy(
  () => import("~/views/foundry.control-panel.analytics")
);
const FoundryControlPanelBranding = lazy(
  () => import("~/views/foundry.control-panel.branding")
);
const FoundryControlPanelBlog = lazy(
  () => import("~/views/foundry.control-panel.blog")
);
const FoundryControlPanelHeader = lazy(
  () => import("~/views/foundry.control-panel.header")
);
const FoundryControlPanelFooter = lazy(
  () => import("~/views/foundry.control-panel.footer")
);
const FoundryControlPanelSearchEngines = lazy(
  () => import("~/views/foundry.control-panel.search-engines")
);
const FoundryControlPanelRedirects = lazy(
  () => import("~/views/foundry.control-panel.redirects")
);
const FoundryControlPanelAuthors = lazy(
  () => import("~/views/foundry.control-panel.authors")
);
const FoundryControlPanelCategories = lazy(
  () => import("~/views/foundry.control-panel.categories")
);
const FoundryControlPanelCta = lazy(
  () => import("~/views/foundry.control-panel.cta")
);
const FoundryControlPanelHostingCloudflare = lazy(
  () => import("~/views/foundry.control-panel.hosting.cloudflare")
);
const FoundryControlPanelHostingVercel = lazy(
  () => import("~/views/foundry.control-panel.hosting.vercel")
);
const FoundrySetupRedirect = lazy(
  () => import("~/views/foundry.control-panel.setup-redirect")
);
const Invitation = lazy(() => import("~/views/invitation"));
const InviteeSignup = lazy(() => import("~/views/invitee-signup"));
const AgencyOnboarding = lazy(() => import("~/views/agencies.onboarding"));
const NoWorkspace = lazy(() => import("~/views/no-workspace"));
const AgencyHome = lazy(() => import("~/views/agencies.home"));
const AgencyPeople = lazy(() => import("~/views/agencies.people"));
const AgencyNewClient = lazy(() => import("~/views/agencies.clients.new"));
const AgencyPitches = lazy(() => import("~/views/agencies.pitches"));
const AgencyPitchOpen = lazy(() => import("~/views/agencies.pitches.open"));
const AgencySettingsLayout = lazy(
  () => import("~/views/layout/AgencySettingsLayout")
);
const AgencySettingsGeneral = lazy(
  () => import("~/views/agencies.settings.general")
);
const AgencySettingsBilling = lazy(
  () => import("~/views/agencies.settings.billing")
);
const AgencySettingsBillingSubscribe = lazy(
  () => import("~/views/agencies.settings.billing.subscribe")
);
const AgencySettingsUsage = lazy(
  () => import("~/views/agencies.settings.usage")
);
const AgencySettingsEntitlements = lazy(
  () => import("~/views/agencies.settings.entitlements")
);
const AgencyClientEntitlements = lazy(
  () => import("~/views/agencies.settings.entitlements.client")
);
const AgencyInvitation = lazy(() => import("~/views/agencies.invitation"));
const AgencyInviteeSignup = lazy(
  () => import("~/views/agencies.invitee-signup")
);
const Error404 = lazy(() => import("~/views/Error404"));
const Error401 = lazy(() => import("~/views/Error401"));
const McpConnect = lazy(() => import("~/views/mcp-connect"));

export default function AppRoutes() {
  return (
    <App>
      <Routes>
        {/* Public routes */}
        <Route path="/invitation" element={<Invitation />} />
        <Route path="/invitee-signup" element={<InviteeSignup />} />
        <Route path="/agencies/invitation" element={<AgencyInvitation />} />
        <Route
          path="/agencies/invitee-signup"
          element={<AgencyInviteeSignup />}
        />
        <Route path="/oauth/authorize" element={<McpConnect />} />
        {/* Guest routes - Login/Signup under GuestLayout */}
        <Route element={<GuestLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signup/complete" element={<SignupComplete />} />
        </Route>

        {/* Protected routes with onboarding guard */}
        <Route element={<OnboardingGuard />}>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/agencies/onboarding" element={<AgencyOnboarding />} />
          <Route path="/no-workspace" element={<NoWorkspace />} />
          <Route
            path="/agency"
            element={<Navigate to="/agency/home" replace />}
          />
          <Route
            path="/agencies"
            element={<Navigate to="/agency/home" replace />}
          />
          <Route
            path="/agencies/clients/new"
            element={<Navigate to="/agency/clients/new" replace />}
          />
          <Route element={<AgencyLayout />}>
            <Route path="/agency/home" element={<AgencyHome />} />
            <Route path="/agency/people" element={<AgencyPeople />} />
            <Route path="/agency/clients/new" element={<AgencyNewClient />} />
            <Route path="/agency/pitches" element={<AgencyPitches />} />
            <Route
              path="/agency/pitches/:teamId"
              element={<AgencyPitchOpen />}
            />
            <Route
              path="/agency/settings"
              element={<Navigate to="/agency/settings/general" replace />}
            />
            <Route element={<AgencySettingsLayout />}>
              <Route
                path="/agency/settings/general"
                element={<AgencySettingsGeneral />}
              />
              <Route
                path="/agency/settings/billing"
                element={<AgencySettingsBilling />}
              />
              <Route
                path="/agency/settings/billing/subscribe"
                element={<AgencySettingsBillingSubscribe />}
              />
              <Route
                path="/agency/settings/billing/usage"
                element={<AgencySettingsUsage />}
              />
              <Route
                path="/agency/settings/entitlements"
                element={<AgencySettingsEntitlements />}
              />
              <Route
                path="/agency/settings/entitlements/:teamId"
                element={<AgencyClientEntitlements />}
              />
            </Route>
          </Route>
          <Route element={<AccountLayout />}>
            <Route path="/profile" element={<Profile />} />
            <Route path="/mcp" element={<Mcp />} />
          </Route>
          <Route element={<AppLayout />}>
            <Route path="/teams/create-new" element={<CreateNewTeam />} />
          </Route>

          <Route path="/t/:teamSlug" element={<TeamRouteGuard />}>
            <Route
              index
              element={<Navigate to="/ai-visibility/home" replace />}
            />
            {/* App routes - only accessible after onboarding */}
            <Route element={<AppLayout />}>
              <Route path="chats/home" element={<Home />} />
              <Route
                path="new-chat"
                element={<Navigate to="/chats/home" replace />}
              />
              <Route
                path="agents/home"
                element={<Navigate to="/chats/home" replace />}
              />
              <Route path="library" element={<Library />} />
              <Route path="keyword-clusters" element={<KeywordClusters />} />
              <Route
                path="keyword-clusters/:reportId"
                element={<KeywordCluster />}
              />
              <Route path="shopping" element={<Shopping />} />
              <Route
                path="shopping/products/:productId"
                element={<ShoppingProduct />}
              />
              <Route
                path="settings"
                element={<Navigate to="/settings/workspace/general" replace />}
              />
              <Route element={<SettingsLayout />}>
                <Route
                  path="settings/workspace"
                  element={
                    <Navigate to="/settings/workspace/general" replace />
                  }
                />
                <Route
                  path="settings/workspace/general"
                  element={<SettingsGeneral />}
                />
                <Route path="settings/workspace/team" element={<Team />} />
                <Route
                  path="settings/workspace/billing"
                  element={<SettingsBilling />}
                />
                <Route
                  path="settings/workspace/billing/subscribe"
                  element={<SettingsBillingSubscribe />}
                />
                <Route
                  path="settings/workspace/billing/usage"
                  element={<SettingsUsage />}
                />

                <Route
                  path="settings/website"
                  element={<Navigate to="/settings/website/sitemap" replace />}
                />
                <Route
                  path="settings/website/sitemap"
                  element={<SettingsWebsiteSitemap />}
                />
                <Route
                  path="settings/website/competitors"
                  element={<SettingsWebsiteCompetitors />}
                />
                <Route
                  path="settings/website/brand-guidelines"
                  element={<SettingsBrandGuidelines />}
                />

                <Route
                  path="settings/integrations"
                  element={<SettingsIntegrations />}
                />
                <Route
                  path="settings/integrations/cloudflare"
                  element={<SettingsIntegrationsCloudflare />}
                />
                <Route
                  path="settings/integrations/vercel"
                  element={<SettingsIntegrationsVercel />}
                />
                <Route
                  path="settings/integrations/google-search-console"
                  element={<SettingsGoogleSearchConsole />}
                />
                <Route
                  path="settings/integrations/google-analytics"
                  element={<SettingsGoogleAnalytics />}
                />
                <Route
                  path="settings/integrations/bing-webmaster-tools"
                  element={<SettingsBingWebmaster />}
                />
                <Route
                  path="settings/integrations/wordpress"
                  element={<SettingsIntegrationsWordPress />}
                />
                <Route
                  path="settings/integrations/sanity"
                  element={<SettingsIntegrationsSanity />}
                />
                <Route
                  path="settings/integrations/shopify"
                  element={<SettingsIntegrationsShopify />}
                />
                <Route
                  path="settings/integrations/google-merchant-center"
                  element={<SettingsIntegrationsGoogleMerchantCenter />}
                />
                <Route
                  path="settings/integrations/product-feed"
                  element={<SettingsIntegrationsProductFeed />}
                />

                <Route
                  path="settings/ai-visibility"
                  element={
                    <Navigate
                      to="/settings/ai-visibility/tracked-platforms"
                      replace
                    />
                  }
                />
                <Route
                  path="settings/ai-visibility/tracked-platforms"
                  element={<SettingsAIVisibility />}
                />
                <Route
                  path="settings/ai-visibility/prompt-tags"
                  element={<SettingsPromptTags />}
                />
                <Route
                  path="settings/ai-visibility/run-history"
                  element={<SettingsRunHistory />}
                />
                <Route
                  path="settings/ai-visibility/run-history/:runId"
                  element={<SettingsRunHistoryDetail />}
                />
              </Route>

              <Route
                path="settings/general"
                element={<Navigate to="/settings/workspace/general" replace />}
              />
              <Route
                path="settings/team"
                element={<Navigate to="/settings/workspace/team" replace />}
              />
              <Route
                path="settings/billing"
                element={<Navigate to="/settings/workspace/billing" replace />}
              />
              <Route
                path="settings/billing/subscribe"
                element={
                  <Navigate
                    to="/settings/workspace/billing/subscribe"
                    replace
                  />
                }
              />
              <Route
                path="settings/prompt-tags"
                element={
                  <Navigate to="/settings/ai-visibility/prompt-tags" replace />
                }
              />
              <Route
                path="settings/run-history"
                element={
                  <Navigate to="/settings/ai-visibility/run-history" replace />
                }
              />
              <Route
                path="settings/run-history/:runId"
                element={
                  <Navigate to="/settings/ai-visibility/run-history" replace />
                }
              />
              <Route
                path="settings/integrations/server-logs"
                element={
                  <Navigate to="/settings/integrations/vercel" replace />
                }
              />
              <Route
                path="settings/brand-guidelines"
                element={
                  <Navigate to="/settings/website/brand-guidelines" replace />
                }
              />
              <Route
                path="settings/google-search-console"
                element={
                  <Navigate
                    to="/settings/integrations/google-search-console"
                    replace
                  />
                }
              />
              <Route
                path="settings/google-analytics"
                element={
                  <Navigate
                    to="/settings/integrations/google-analytics"
                    replace
                  />
                }
              />
              <Route
                path="settings/bing-webmaster-tools"
                element={
                  <Navigate
                    to="/settings/integrations/bing-webmaster-tools"
                    replace
                  />
                }
              />
              <Route element={<FoundryLayout />}>
                <Route
                  path="foundry"
                  element={<Navigate to="/foundry/blogs" replace />}
                />
                <Route path="foundry/blogs" element={<FoundryBlogs />} />
                <Route
                  path="foundry/blogs/:blogId"
                  element={<FoundryBlogEditor />}
                />
                <Route
                  path="foundry/landing-pages"
                  element={<FoundryLandingPages />}
                />
                <Route
                  path="foundry/landing-pages/:pageId"
                  element={<FoundryLandingPageEditor />}
                />
                <Route path="foundry/tools" element={<FoundryTools />} />
                <Route
                  path="foundry/tools/:toolId"
                  element={<FoundryToolEditor />}
                />
                <Route
                  path="foundry/lead-forms"
                  element={<FoundryLeadForms />}
                />
                <Route
                  path="foundry/lead-forms/:formId"
                  element={<FoundryLeadFormEditor />}
                />
                <Route element={<FoundryControlPanelLayout />}>
                  <Route
                    path="foundry/control-panel"
                    element={
                      <Navigate to="/foundry/control-panel/general" replace />
                    }
                  />
                  <Route
                    path="foundry/control-panel/general"
                    element={<FoundryControlPanelGeneral />}
                  />
                  <Route
                    path="foundry/control-panel/analytics"
                    element={<FoundryControlPanelAnalytics />}
                  />
                  <Route
                    path="foundry/control-panel/branding"
                    element={<FoundryControlPanelBranding />}
                  />
                  <Route
                    path="foundry/control-panel/blog"
                    element={<FoundryControlPanelBlog />}
                  />
                  <Route
                    path="foundry/control-panel/header"
                    element={<FoundryControlPanelHeader />}
                  />
                  <Route
                    path="foundry/control-panel/footer"
                    element={<FoundryControlPanelFooter />}
                  />
                  <Route
                    path="foundry/control-panel/search-engines"
                    element={<FoundryControlPanelSearchEngines />}
                  />
                  <Route
                    path="foundry/control-panel/redirects"
                    element={<FoundryControlPanelRedirects />}
                  />
                  <Route
                    path="foundry/control-panel/website"
                    element={
                      <Navigate to="/foundry/control-panel/branding" replace />
                    }
                  />
                  <Route
                    path="foundry/control-panel/authors"
                    element={<FoundryControlPanelAuthors />}
                  />
                  <Route
                    path="foundry/control-panel/categories"
                    element={<FoundryControlPanelCategories />}
                  />
                  <Route
                    path="foundry/control-panel/cta"
                    element={<FoundryControlPanelCta />}
                  />
                  <Route
                    path="foundry/control-panel/setup"
                    element={<FoundrySetupRedirect />}
                  />
                  <Route
                    path="foundry/control-panel/setup/cloudflare"
                    element={<FoundryControlPanelHostingCloudflare />}
                  />
                  <Route
                    path="foundry/control-panel/setup/vercel"
                    element={<FoundryControlPanelHostingVercel />}
                  />
                  <Route
                    path="foundry/control-panel/go-live"
                    element={
                      <Navigate to="/foundry/control-panel/setup" replace />
                    }
                  />
                  <Route
                    path="foundry/control-panel/go-live/cloudflare"
                    element={
                      <Navigate
                        to="/foundry/control-panel/setup/cloudflare"
                        replace
                      />
                    }
                  />
                  <Route
                    path="foundry/control-panel/go-live/vercel"
                    element={
                      <Navigate
                        to="/foundry/control-panel/setup/vercel"
                        replace
                      />
                    }
                  />
                  <Route
                    path="foundry/control-panel/hosting"
                    element={
                      <Navigate to="/foundry/control-panel/setup" replace />
                    }
                  />
                  <Route
                    path="foundry/control-panel/hosting/cloudflare"
                    element={
                      <Navigate
                        to="/foundry/control-panel/setup/cloudflare"
                        replace
                      />
                    }
                  />
                  <Route
                    path="foundry/control-panel/hosting/vercel"
                    element={
                      <Navigate
                        to="/foundry/control-panel/setup/vercel"
                        replace
                      />
                    }
                  />
                </Route>
              </Route>
            </Route>

            {/* Chat routes - uses ChatLayout for auto-scrolling on stream */}
            <Route element={<ChatLayout />}>
              <Route path="chats/:chatId" element={<Chat />} />
            </Route>

            {/* AI Visibility routes - uses AIVisibilityLayout */}
            <Route element={<AIVisibilityLayout />}>
              <Route
                path="ai-visibility"
                element={<Navigate to="/ai-visibility/home" replace />}
              />
              <Route path="ai-visibility/home" element={<AIVisibility />} />
              <Route
                path="ai-visibility/prompts"
                element={<AIVisibilityPrompts />}
              />
              <Route
                path="ai-visibility/prompts/suggestions"
                element={<AIVisibilityPromptsSuggestions />}
              />
              <Route
                path="ai-visibility/prompts/removals"
                element={<AIVisibilityPromptsRemovals />}
              />
              <Route
                path="ai-visibility/prompts/:id"
                element={<AIVisibility />}
              />

              <Route
                path="ai-visibility/sources"
                element={
                  <Navigate to="/ai-visibility/sources/domains" replace />
                }
              />

              <Route
                path="ai-visibility/sources/:tab"
                element={<AIVisibilitySources />}
              />

              <Route
                path="ai-visibility/fanouts"
                element={<AIVisibilityFanouts />}
              />

              <Route
                path="ai-visibility/signals"
                element={<AIVisibilitySignals />}
              />
              <Route
                path="ai-visibility/signals/:signalId"
                element={<AIVisibilitySignals />}
              />

              <Route
                path="ai-visibility/ai-traffic-analytics"
                element={<AIVisibilityBotTraffic />}
              />
              <Route
                path="ai-visibility/responses/:responseId"
                element={<AIVisibilityLLMResponse />}
              />
            </Route>
          </Route>

          {TEAM_SCOPED_SECTIONS.map((section) => (
            <Route
              key={section}
              path={`/${section}/*`}
              element={<LegacyTeamRedirect />}
            />
          ))}

          {/* Onboarding routes - only accessible if not onboarded */}
          <Route element={<OnboardingLayout />}>
            <Route path="/onboarding" element={<Onboarding />}>
              <Route path="website" element={<OnboardingWebsite />} />
              <Route path="competitors" element={<OnboardingCompetitors />} />
              <Route path="store" element={<OnboardingStore />} />
              <Route
                path="catalog-prompts"
                element={<OnboardingCatalogPrompts />}
              />
              <Route path="prompts" element={<OnboardingPrompts />} />
              <Route path="integrations" element={<OnboardingIntegrations />} />
              <Route path="questions" element={<OnboardingQuestions />} />
            </Route>
          </Route>
        </Route>

        <Route path="/401" element={<Error401 />} />
        <Route path="*" element={<Error404 />} />
      </Routes>
    </App>
  );
}
